import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Can } from '@/components/Can';
import { FilterBar } from '@/components/FilterBar';
import { ImportExportMenu } from '@/components/ImportExportMenu';
import { RefreshButton } from '@/components/RefreshButton';
import { api } from '@/lib/api';
const STATE_BADGES = {
    draft: 'bg-muted text-muted-foreground',
    pending_approval: 'bg-amber-100 text-amber-700',
    approved: 'bg-blue-100 text-blue-700',
    sent: 'bg-blue-100 text-blue-700',
    viewed: 'bg-indigo-100 text-indigo-700',
    partially_paid: 'bg-yellow-100 text-yellow-700',
    paid: 'bg-green-100 text-green-700',
    overdue: 'bg-rose-100 text-rose-700',
    voided: 'bg-zinc-200 text-zinc-700',
    cancelled: 'bg-zinc-200 text-zinc-700',
};
export function InvoiceListPage() {
    const [filters, setFilters] = useState({ state: '', from: '', to: '', q: '' });
    const state = filters.state ?? '';
    const from = filters.from ?? '';
    const to = filters.to ?? '';
    const search = (filters.q ?? '').toLowerCase().trim();
    const invoices = useQuery({
        queryKey: ['invoices', state, from, to],
        queryFn: async () => {
            const p = new URLSearchParams();
            if (state)
                p.set('state', state);
            if (from)
                p.set('from', from);
            if (to)
                p.set('to', to);
            return (await api.get(`/sales/invoices/?${p.toString()}`)).data;
        },
    });
    const filtered = useMemo(() => {
        if (!search)
            return invoices.data ?? [];
        return (invoices.data ?? []).filter((i) => (i.number ?? '').toLowerCase().includes(search)
            || (i.reference ?? '').toLowerCase().includes(search)
            || (i.customer_name ?? '').toLowerCase().includes(search));
    }, [invoices.data, search]);
    return (_jsxs("div", { className: "space-y-4", children: [_jsxs("header", { className: "flex items-center justify-between", children: [_jsxs("div", { children: [_jsx("h1", { className: "text-2xl font-semibold", children: "Invoices" }), _jsx("p", { className: "text-sm text-muted-foreground", children: "Draft, posted, sent, and paid invoices." })] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx(RefreshButton, { queryKeys: [['invoices']], label: "Fetch" }), _jsx(ImportExportMenu, { resource: "/sales/invoices", canImport: false }), _jsx(Can, { code: "sales.invoice.create", children: _jsx(Link, { to: "/sales/invoices/new", className: "rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground", children: "+ Invoice" }) })] })] }), _jsx(FilterBar, { filters: [
                    { key: 'q', kind: 'search', placeholder: 'Search number / reference / customer…' },
                    {
                        key: 'state', kind: 'select', label: 'State',
                        options: Object.keys(STATE_BADGES).map((s) => ({ value: s, label: s.replace(/_/g, ' ') })),
                    },
                    { key: 'from', kind: 'date', label: 'From' },
                    { key: 'to', kind: 'date', label: 'To' },
                ], value: filters, onChange: setFilters }), _jsxs("ul", { className: "divide-y rounded-md border bg-card", children: [filtered.map((inv) => (_jsx("li", { children: _jsxs(Link, { to: `/sales/invoices/${inv.id}`, className: "flex items-center justify-between gap-4 px-4 py-3 hover:bg-muted/30", children: [_jsxs("span", { className: "grid grid-cols-[120px_120px_1fr] items-center gap-4", children: [_jsx("span", { className: "font-mono text-sm", children: inv.number || '—' }), _jsx("span", { className: "text-sm text-muted-foreground", children: inv.date }), _jsx("span", { className: "font-medium", children: inv.reference || inv.customer_name || '(no reference)' })] }), _jsxs("span", { className: "flex items-center gap-3", children: [_jsxs("span", { className: "font-mono text-sm", children: [inv.grand_total, " ", inv.currency] }), _jsx("span", { className: `rounded px-2 py-0.5 text-[10px] uppercase tracking-wide ${STATE_BADGES[inv.state] ?? ''}`, children: inv.state })] })] }) }, inv.id))), !invoices.isLoading && filtered.length === 0 && (_jsx("li", { className: "px-4 py-8 text-center text-sm text-muted-foreground", children: "No invoices match." }))] })] }));
}
